import { ProductImageCarousel } from "../../../../shared/pdp/components/ProductImageCarousel";
import { ProductVariantSelect } from "../../../../shared/pdp/components/ProductVariantSelect";
import { ProductPrice } from "../../../../shared/pdp/components/ProductPrice";
import { IncludedProduct, type IncludedProductData } from "../IncludedProduct";
import { LoadingSpinner } from "../../../../shared/pdp/components/LoadingSpinner";
import { PaymentMethods } from "../../../../shared/pdp/components/PaymentMethods";
import type { PersonalizedOnetimeBundle } from "@halm-club/halm-shared";
import { useRef } from "react";
import { useStickyAddToCartVisibility } from "../../../../shared/pdp/components/useStickyAddToCartVisibility";
import { StickyAddToCartBar } from "../../../../shared/pdp/components/StickyAddToCartBar";
import "./PdpBuyBox.css";
import { FaqListItem } from "../../../../shared/pdp/components/FaqListItem";
import { BuyBoxSocialProof } from "../BuyBoxSocialProof";

type BundleVariantOption = PersonalizedOnetimeBundle["variants"][number];

const getStarCount = (numberOfStars: number) =>
  Math.min(Math.max(Math.round(numberOfStars), 0), 5);

type BundleBuyBoxProps = {
  buyerCount?: number;
  compareAtPrice: number;
  discountPercentage: number;
  heading: unknown;
  imageSources: string[];
  includedProducts: IncludedProductData[];
  isAddingToCart: boolean;
  isSkipVisible: boolean;
  onAddToCart: () => void;
  onSkip: () => void;
  onVariantChange: (variantId: string) => void;
  price: number;
  productId: string;
  selectedVariantId: string;
  statusMessage: string;
  subheading?: string;
  tags: string[];
  buttonSubText?: string;
  title: string;
  titleTag?: string;
  variants: BundleVariantOption[];
  informationItems: PersonalizedOnetimeBundle["collapsibleInformation"][number][];
  reviewContent: PersonalizedOnetimeBundle["reviewContent"];
};

export const PdpBuyBox = ({
  buyerCount,
  compareAtPrice,
  discountPercentage,
  heading,
  imageSources,
  includedProducts,
  isAddingToCart,
  isSkipVisible,
  onAddToCart,
  onSkip,
  onVariantChange,
  price,
  productId,
  selectedVariantId,
  statusMessage,
  subheading,
  tags,
  buttonSubText,
  title,
  titleTag,
  variants,
  informationItems,
  reviewContent,
}: BundleBuyBoxProps) => {
  const regularButtonRef = useRef<HTMLButtonElement>(null);
  const isStickyAddToCartVisible =
    useStickyAddToCartVisibility(regularButtonRef);
  return (
    <>
      <div className="react-pdp__buybox">
        <div className="react-pdp__onetime-card">
          <ProductImageCarousel imageAlt={title} imageSources={imageSources} />

          <div className="react-pdp__onetime-main">
            <div className="react-pdp__onetime-purchase">
              <div className="react-pdp__buybox-bundle-content">
                <div className="react-pdp__title-and-mobile-price">
                  <div className="react-pdp__onetime-title-row">
                    <h2
                      className="react-pdp__onetime-title"
                      data-test="bundle-title"
                    >
                      {title}
                    </h2>
                    {titleTag ? (
                      <span className="react-pdp__onetime-title-tag">
                        {titleTag}
                      </span>
                    ) : null}
                  </div>
                  <ProductVariantSelect
                    ariaLabel="Bundle-Größe"
                    onChange={onVariantChange}
                    optionLabel={(variant) =>
                      `Bundle für ${variant.lawnSize} m²`
                    }
                    selectedVariantId={selectedVariantId}
                    variants={variants}
                    className="mobile"
                  />
                </div>

                {subheading ? (
                  <p className="react-pdp__onetime-subheading">{subheading}</p>
                ) : null}

                {includedProducts.length ? (
                  <ol className="react-pdp__onetime-included-list">
                    {includedProducts.map((product, index) => (
                      <IncludedProduct
                        index={index}
                        key={`${product.title}-${index}`}
                        product={product}
                      />
                    ))}
                  </ol>
                ) : null}
              </div>

              <ProductPrice
                compareAtPrice={compareAtPrice}
                dataTest="bundle-price"
                price={price}
                className="mobile"
                discountTag="Bundle-Vorteil"
              />

              <BuyBoxSocialProof
                buyerCount={buyerCount}
                rating={reviewContent?.rating}
              />

              <div className="react-pdp__variant-price-box">
                <ProductVariantSelect
                  ariaLabel="Bundle-Größe"
                  onChange={onVariantChange}
                  optionLabel={(variant) => `Bundle für ${variant.lawnSize} m²`}
                  selectedVariantId={selectedVariantId}
                  variants={variants}
                  className="desktop"
                />
                <ProductPrice
                  compareAtPrice={compareAtPrice}
                  dataTest="bundle-price"
                  price={price}
                  className="desktop"
                  discountTag="Bundle-Vorteil"
                />
                <div className="react-pdp__onetime-button-wrap">
                  <button
                    className="react-pdp__onetime-button"
                    data-test="bundle-add-to-cart"
                    disabled={isAddingToCart}
                    onClick={onAddToCart}
                    ref={regularButtonRef}
                    type="button"
                  >
                    {isAddingToCart ? <LoadingSpinner /> : null}
                    <span>In den Warenkorb</span>
                  </button>
                  {buttonSubText ? (
                    <p className="react-pdp__onetime-next-step">
                      {buttonSubText}
                    </p>
                  ) : null}
                  {isSkipVisible ? (
                    <button
                      className="react-pdp__onetime-skip-button"
                      onClick={onSkip}
                      type="button"
                    >
                      Überspringen
                    </button>
                  ) : null}
                  {statusMessage ? (
                    <p className="react-pdp__onetime-error" role="alert">
                      {statusMessage}
                    </p>
                  ) : null}
                  <PaymentMethods />
                </div>
              </div>
            </div>
            <div className="react-pdp__bundle-information-desktop">
              <div className="react-pdp__onetime-faq-list">
                {informationItems.map((item, index) => (
                  <FaqListItem
                    description={item.description}
                    key={`${item.title}-${index}`}
                    title={item.title}
                  />
                ))}
              </div>
            </div>
            {reviewContent && reviewContent.featuredReview ? (
              <div className="react-pdp__onetime-featured-review desktop">
                {reviewContent.featuredReview.heading ? (
                  <div className="react-pdp__onetime-review-label">
                    <span
                      aria-hidden="true"
                      className="material-symbols-rounded react-pdp__onetime-review-label-icon"
                    >
                      chat
                    </span>
                    <h3>{reviewContent.featuredReview.heading}</h3>
                  </div>
                ) : null}

                <article className="react-pdp__onetime-review-card">
                  <div className="react-pdp__onetime-review-author-row">
                    {reviewContent.featuredReview.imageSource ? (
                      <img
                        alt=""
                        className="react-pdp__onetime-review-image"
                        src={reviewContent.featuredReview.imageSource}
                      />
                    ) : null}
                    <div className="react-pdp__onetime-review-author">
                      <div className="react-pdp__onetime-review-author-name">
                        <strong>{reviewContent.featuredReview.author}</strong>
                        <span
                          aria-label="Verifizierte Bewertung"
                          className="react-pdp__onetime-review-verified"
                          role="img"
                        >
                          <span
                            aria-hidden="true"
                            className="material-symbols-rounded"
                          >
                            check
                          </span>
                        </span>
                      </div>
                      <span className="react-pdp__onetime-review-place">
                        {reviewContent.featuredReview.place}
                      </span>
                      <div
                        className="react-pdp__onetime-review-stars"
                        aria-label={`${getStarCount(reviewContent.featuredReview.numberOfStars)} von 5 Sternen`}
                      >
                        {Array.from(
                          {
                            length: getStarCount(
                              reviewContent.featuredReview.numberOfStars,
                            ),
                          },
                          (_, index) => (
                            <span
                              aria-hidden="true"
                              className="material-symbols-rounded filled-icon"
                              key={index}
                            >
                              star
                            </span>
                          ),
                        )}
                      </div>
                    </div>
                  </div>
                  <p className="react-pdp__onetime-review-quote">
                    “{reviewContent.featuredReview.reviewContent}”
                  </p>
                </article>

                {reviewContent.linkToReviews ? (
                  <a
                    className="react-pdp__onetime-reviews-link"
                    href={reviewContent.linkToReviews}
                    target="_blank"
                  >
                    Alle {reviewContent.numberOfReviews} Halm Bewertungen
                    ansehen →
                  </a>
                ) : null}
              </div>
            ) : null}
          </div>
        </div>
      </div>
      {isStickyAddToCartVisible ? (
        <StickyAddToCartBar
          compareAtPrice={compareAtPrice}
          isAddingToCart={isAddingToCart}
          onAddToCart={onAddToCart}
          onSkip={isSkipVisible ? onSkip : undefined}
          price={price}
          skipLabel="Überspringen"
          title={title}
          titleTag={titleTag}
        />
      ) : null}
    </>
  );
};
